import { getOperationElements, mergeCloseOperations, annotateOperations } from '../../../shared/lib/map-operation-utils.js';
import { nui } from 'lib/nodes-ui';
import { appUI } from 'lib/app-ui';
import { zoomUI } from 'lib/zoom-ui';
import { colorFromUserId } from 'lib/util';
import { MapStore } from 'store';
import moment from 'moment';

class HistoryUI {

  constructor() {
    this.opLogPointer = false;
    this.operationLog = [];
    this.invertedOperationLog = [];
    this.notifications = [];
    this.notificationsPointer = 0;

    this.inPlayback = false;

    // accumulated operations since page load
    this.collectedOperations = [];
  }

  setCanvas(canvas) {
    this.canvas = canvas;
    this.operation = operation;
  }

  setRecentActivity(recent) {
    this.operation.setHistoryUI(this);
    if (!recent.operations.length) return;
    const prepped = this.prepOperationsForPlayback(recent.operations);
    if (!prepped.log.length) return;
    this.operationLog = prepped.log;
    this.invertedOperationLog = prepped.invertedLog;
    this.opLogPointer = this.operationLog.length - 1;
    this.notifications = recent.notifications;
    this.notificationsPointer = recent.notifications.length;
    this.semanticZoomEnabled = zoomUI.semanticZoomEnabled;
  }

  prepOperationsForPlayback(operations) {
    const log = [];
    const invertedLog = [];

    const trimmed = this.trimDeprecatedOperations(operations);

    for (const op of trimmed) {
      const inverted = this.operation.ur.invert(op);
      if (!inverted) continue; // skip ops that can't reverse
      log.push(op);
      invertedLog.push(inverted);
    }

    return { log, invertedLog };
  }

  trimDeprecatedOperations(operations) {
    const tagOp = /^.*?tag.*?$/i;
    return operations.filter(o =>
      !o.operation.match(tagOp) && !(o.messages && o.messages.every(m => m.operation.match(tagOp)))
    );
  }

  enterPlaybackMode() {
    if (this.semanticZoomEnabled) zoomUI.disableSemanticZoom();
    if (this.inPlayback) return;
    this.inPlayback = true;
    this.canonicalStore = appUI.cloneStore();
    appUI.setViewOnly();
  }

  leavePlaybackMode() {
    if (!this.inPlayback) return;
    this.opLogPointer = this.operationLog.length - 1;
    this.canvas.notificationsTag.playbackPointer = this.opLogPointer;
    this.inPlayback = false;
    appUI.loadStore(this.canonicalStore.dump());
    appUI.unsetViewOnly();
    appUI.freshen();
    if (this.semanticZoomEnabled) zoomUI.enableSemanticZoom();
    this.canonicalStore = false;
  }

  operationCollector(op) {
    const prepped = this.prepOperationsForPlayback([op]);
    if (!prepped.log.length) return;

    this.operationLog.push(prepped.log[0]);
    this.collectedOperations.push(prepped.log[0]);
    this.invertedOperationLog.push(prepped.invertedLog[0]);
    this.updateNotifications(this.collectedOperations);
    if (this.canvas.notificationsTag) {
      if (!this.inPlayback) {
        this.opLogPointer = this.operationLog.length - 1;
        this.canvas.notificationsTag.playbackPointer = this.opLogPointer;
      }
      this.canvas.notificationsTag.update();
      this.canvas.appHeaderTag.update();
    }
  }

  updateNotifications(operations) {
    // lots of copy-paste from lib/map/notifications...

    if (!operations) return;
    const cleanOperations = mergeCloseOperations({ operations, secondsApart: 360 });
    if (!cleanOperations) return;
    annotateOperations(cleanOperations);


    const notifications = [];
    for (const op of cleanOperations) {
      if (op.messages && op.messages.length) {
        const elements = getOperationElements(op);

        notifications.push({
          friendlyTime: moment(op.createdTime).fromNow(),
          opIds: op.messages.map(m => m.opId),
          user: op.user.id === window.user_id ? window.user : this.canvas.presence.getPresence(op.user.id),
          readableSummary: op.readableSummary,
          elements,
        });
      }
    }

    this.notifications.length = this.notificationsPointer;
    this.notifications = this.notifications.concat(notifications);
  }

  atEnd() {
    return this.opLogPointer === this.operationLog.length;
  }

  async fastForward(deferUpdate) {
    if (this.atEnd()) {
      console.log(`can't fastFowrard past end`);
      return;
    }
    this.opLogPointer = this.opLogPointer + 1;
    const op = this.operationLog[this.opLogPointer];

    if (op) {
      await this.triggerOperation(op, deferUpdate);
    }
  }

  atBeginning() {
    return this.opLogPointer === -1;
  }

  async rewind(deferUpdate) {
    if (this.atBeginning()) {
      console.log(`can't rewind past beginning`);
      return;
    }
    const op = this.invertedOperationLog[this.opLogPointer];
    if (op) {
      await this.triggerOperation(op, deferUpdate);
    }
    this.opLogPointer = this.opLogPointer - 1;
  }

  async triggerOperation(op, deferUpdate) {
    op._fromPlayback = true;
    this.currentOperation = op;
    try {
      await this.operation.operationHandler(JSON.parse(JSON.stringify(op)), deferUpdate);
    } catch (e) {
      console.log(`Can't trigger ${op.operation}: ${e}`);
      console.log('Unapplied operation: ', JSON.stringify(op, null, 2));
    }
  }

  getNotificationElements(notification) {
    if (!notification) return {};
    if (!notification.elements) return {};

    let nodes;
    let links;
    let regions;
    let comments;

    if (notification.elements.nodeIds) {
      nodes = notification.elements.nodeIds.map(id => this.canvas.store.getNode(id)).filter(e => e);
    }
    if (notification.elements.linkIds) {
      links = notification.elements.linkIds.map(id => this.canvas.store.getLink(id)).filter(e => e);
    }
    if (notification.elements.regionIds) {
      regions = notification.elements.regionIds.map(id => this.canvas.store.getRegion(id)).filter(e => e);
    }
    if (notification.elements.commentIds) {
      comments = notification.elements.commentIds.map(id => this.canvas.store.getComment(id)).filter(e => e);
    }
    return {nodes, links, regions, comments};
  }

  zoomToCurrentOperation() {
    const operation = this.currentOperation;

    if (!operation) return;

    const elements = getOperationElements(operation);

    if (!elements) return;

    const { nodes, links, regions } = hui.getNotificationElements({ elements });

    if (!nodes && !links && !regions) return;

    this.highlightElements(elements, operation.user && operation.user.id);

    this.canvas.centerContent({ nodes, links, regions }, { animate: true });
  }

  zoomToFitAllChanges() {
    if (!this.operationLog || !this.operationLog.length) {
      this.canvas.fitMapContents();
      return;
    }
    const nodeIds = {};
    const linkIds = {};
    const regionIds = {};
    for (const op of this.operationLog) {
      if (!op || !op.elements || !(op.elements.nodeIds && !op.elements.linkIds && !op.elements.regionIds)) continue;
      for (const id of op.elements.nodeIds) {
        nodeIds[id] = 1;
      }
      for (const id of op.elements.linkIds) {
        linkIds[id] = 1;
      }
      for (const id of op.elements.regionIds) {
        regionIds[id] = 1;
      }
    }
    const nodes = (Object.keys(nodeIds)).map(id => this.canvas.store.getNode(id));
    const links = (Object.keys(linkIds)).map(id => this.canvas.store.getLink(id));
    const regions = (Object.keys(regionIds)).map(id => this.canvas.store.getRegion(id));
    this.canvas.centerContent({ nodes, regions, links }, {animate: true});
  }

  highlightElements(elements, userId) {
    const color = colorFromUserId(userId);
    appUI.highlightElements(elements, color);
  }

  unhighlightElements(elements) {
    appUI.unhighlightElements(elements);
    this.showingOperationDiff = null;
  }

  async shiftHistory({ to, deferUpdate }) {
    if (to === hui.opLogPointer) {
      console.log('pointers equal');
      return;
    }
    const steps = Math.abs(to - this.opLogPointer);
    if (to < this.opLogPointer) {
      for (let i = 0; i < steps; i++) {
        await this.rewind(deferUpdate);
      }
    } else {
      for (let i = 0; i < steps; i++) {
        await this.fastForward(deferUpdate);
      }
    }
  }

  /*
    once we send this, if the user leaves the map, they'll see "catch me up" info with all changes since this operation
  */
  sendLastOperationSeen() {
    this.canvas.operation.sender.sendOperationReceivedMessage({
      operationId: this.canvas.operation.sender.currentSequence
    });
  }
}

const hui = new HistoryUI();
window.hui = hui;
export {HistoryUI, hui};



// WEBPACK FOOTER //
// ./starling/src/lib/history-ui.js